// Render nested rows

import { Fragment, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ITableColumn } from "../../interfaces/Table/TableColumn";
import { IModifiedPosition } from "./model";
import { renderCellContentImpl } from "./OptionPositionTableRow.cell";
import {
  computeGreeksSummary,
  computePositionValuePnlSummary,
  getColumnDataKey,
} from "./OptionPositionTableRow.helpers";
import { renderHeaderCellContentImpl } from "./OptionPositionTableRow.headerCell";
import { IOptionPositionTableRowProps } from "./OptionPositionTableRow.types";
import {
  AnimatedColumn,
  PositionTableRow,
  HeaderColumn,
} from "./style";

function OptionPositionTableRow({
  inLiquidation,
  row,
  columns,
  actionButtonId,
  onClickPosition,
  onClosePosition,
  onViewPositionDetails,
  onSharePosition,
}: IOptionPositionTableRowProps) {
  const [expanded, setExpanded] = useState(false);
  const [expandedExpiries, setExpandedExpiries] = useState<string[]>();
  const { t } = useTranslation("app", { keyPrefix: "OptionPositionTableRow" });

  const { nestedOptionsData, asset } = row.original;
  const allPositions = Object.values(nestedOptionsData || {})
    //@ts-ignore
    .map((v) => v.positions)
    .flat();

  const onToggleExpandedExpiry = useCallback(
    (expiry: string) => {
      if ((expandedExpiries || []).includes(expiry)) {
        setExpandedExpiries(
          (expandedExpiries || []).filter((exp) => exp !== expiry)
        );
      } else {
        setExpandedExpiries([...(expandedExpiries || []), expiry]);
      }
    },
    [expandedExpiries]
  );

  // Returns the summed greeks
  const greeksSummary = useMemo(() => {
    return computeGreeksSummary(nestedOptionsData);
  }, [nestedOptionsData]);

  // Returns the summed greeks
  const positionValuePnlSummary = useMemo(() => {
    return computePositionValuePnlSummary(nestedOptionsData);
  }, [nestedOptionsData]);

  const renderHeaderCellContent = useCallback(
    (column: ITableColumn<IModifiedPosition>, expiry?: string) =>
      renderHeaderCellContentImpl(column, expiry, {
        asset,
        t,
        expanded,
        allPositions,
        expandedExpiries,
        positionValuePnlSummary,
        greeksSummary,
      }),
    [
      asset,
      t,
      expanded,
      allPositions.length,
      expandedExpiries,
      positionValuePnlSummary,
      greeksSummary,
    ]
  );

  const renderCellContent = useCallback(
    (pos: IModifiedPosition, column: ITableColumn<IModifiedPosition>) =>
      renderCellContentImpl(pos, column, {
        actionButtonId,
        inLiquidation,
        onClosePosition,
        onSharePosition,
        onViewPositionDetails,
        t,
      }),
    [
      actionButtonId,
      inLiquidation,
      onClosePosition,
      onSharePosition,
      onViewPositionDetails,
      t,
    ]
  );

  if (!nestedOptionsData) {
    return null;
  }

  const { key, ...rowProps } = row.getRowProps();
  return (
    <>
      <PositionTableRow
        key={key}
        {...rowProps}
        style={{
          position: "relative",
          zIndex: 2,
        }}
        onClick={() => {
          setExpanded((prev) => !prev);
        }}
      >
        {columns.map((col, i) => (
          <HeaderColumn
            key={String(col.accessor || col.id || i)}
            data-column={getColumnDataKey(col)}
          >
            {renderHeaderCellContent(col)}
          </HeaderColumn>
        ))}
      </PositionTableRow>
      {/* Expiries rows + cell */}
      {
        // Loop all expiries
        Object.keys(nestedOptionsData)
          .sort((a, b) => Number(a) - Number(b))
          .map((e) => (
            <Fragment key={e}>
              <PositionTableRow
                onClick={() => onToggleExpandedExpiry(e)}
                style={{
                  position: "relative",
                  zIndex: 1,
                  height: "auto",
                }}
              >
                {columns.map((col, i) => (
                  <HeaderColumn
                    key={String(col.accessor || col.id || i)}
                    data-column={getColumnDataKey(col)}
                  >
                    {renderHeaderCellContent(col, e)}
                  </HeaderColumn>
                ))}
              </PositionTableRow>
              {
                // Check if expiry expanded
                nestedOptionsData[e].positions.map((p) => {
                  const { option } = p;
                  if (!option) {
                    return null;
                  }
                  return (
                    <PositionTableRow
                      key={p.instrument_name}
                      style={{
                        position: "relative",
                        zIndex: 0,
                        height: "auto",
                      }}
                      onClick={() => onClickPosition(p)}
                    >
                      {columns.map((c, i) => (
                        <AnimatedColumn
                          key={String(c.accessor || c.id || i)}
                          data-column={getColumnDataKey(c)}
                          expanded={expanded && expandedExpiries?.includes(e)}
                        >
                          {renderCellContent(p, c)}
                        </AnimatedColumn>
                      ))}
                    </PositionTableRow>
                  );
                })
              }
            </Fragment>
          ))
      }
    </>
  );
}

export default OptionPositionTableRow;
